import { QueryResult, useQuery } from '@apollo/client'
import * as React from 'react'
import Button from '../../../../common/src/components/Button'
import { InlineErrorBoundary } from '../../../../common/src/components/InlineErrorBoundary'
import { TasksTableMultiselect } from '../../../../common/src/components/TasksTableMultiselect'
import TypeaheadBox from '../../../../common/src/components/TypeaheadBox'
import {
  AlignRight,
  BodyMonoTextSpan,
  BodyTextSpan,
  EllipsisDiv,
  H1,
  H3,
  H5,
  IntroText,
  Spacer,
} from '../../../../common/src/components/core'
import { handleError } from '../../../../common/src/data/errors'
import { fetchFirmTasksQuery } from '../../../../common/src/data/queries'
import {
  BulkUpdateOp,
  FetchFirmTasksQuery,
  FetchFirmTasksQueryVariables,
  LeasingAttributionSource,
  UpdateTasksMutation,
} from '../../../../common/src/types.gen'
import { bulkUpdateTasks } from '../../graphql/mutations'
import { getTasksNotUpdated, renderFieldError, renderTasksList } from './BulkUpdatePage'

interface MarketingSourcesSectionProps {
  firmId?: number
  onUpdate: () => void
}

interface MarketingSourcesImplProps {
  onUpdate: () => void
}

type LeasingTask = UpdateTasksMutation['bulkUpdateTasks'][0]

interface MarketingSourcesSectionState {
  selectedSource?: LeasingAttributionSource
  selectedTaskIds: Set<string | number>
  errorSource: string
  errorTaskIds: string
  submitting: boolean
  saveSuccess: boolean
  updatedTasks: LeasingTask[]
}

class MarketingSourcesSectionImpl extends React.PureComponent<
  MarketingSourcesImplProps &
    FetchFirmTasksQueryVariables &
    QueryResult<FetchFirmTasksQuery, FetchFirmTasksQueryVariables>,
  MarketingSourcesSectionState
> {
  state = {
    selectedSource: undefined,
    selectedTaskIds: new Set<string | number>(),
    errorSource: '',
    errorTaskIds: '',
    submitting: false,
    saveSuccess: false,
    updatedTasks: [] as LeasingTask[],
  }

  private onSelectedTaskIdsChange = (selectedTaskIds: Set<string | number>) => this.setState({ selectedTaskIds })

  private marketingSourceLoad = (prefix: string) => {
    const sources = Object.keys(LeasingAttributionSource)
    const matchSources = sources.filter(s => s.toLowerCase().startsWith(prefix.toLowerCase())).slice(0, 10)

    const matches = matchSources.map(s => LeasingAttributionSource[s as keyof typeof LeasingAttributionSource])
    return Promise.resolve(matches)
  }

  private marketingSourceRow = (source: LeasingAttributionSource) => (
    <IntroText>
      <EllipsisDiv>
        <BodyTextSpan>(New #) &middot; {source}</BodyTextSpan>
      </EllipsisDiv>
    </IntroText>
  )

  private onChooseMarketingSource = (selectedSource: LeasingAttributionSource) => {
    this.setState({ selectedSource })
  }

  private onSaveUpdate = () => {
    const { onUpdate } = this.props
    const { selectedSource, selectedTaskIds } = this.state

    this.setState({ submitting: true })

    let errorSource = ''
    let errorTaskIds = ''

    if (!selectedSource) {
      errorSource = 'must select a marketing source'
    }

    if (selectedTaskIds.size === 0) {
      errorTaskIds = 'must select a property'
    }

    this.setState({ errorSource, errorTaskIds })

    if (errorSource || errorTaskIds) {
      this.setState({ submitting: false })
      return
    }

    const taskIds = Array.from(selectedTaskIds).map(id => parseInt(id.toString(), 10))

    bulkUpdateTasks({
      taskIds,
      field: BulkUpdateOp.LEASING_ATTRIBUTION_SOURCE,
      value: selectedSource,
    })
      .then(res => {
        onUpdate()
        this.setState({ saveSuccess: true, submitting: false, updatedTasks: res.data!.bulkUpdateTasks })
      })
      .catch(handleError)
  }

  private marketingSourcesForm = () => {
    const { data, firmId } = this.props
    const { submitting, errorSource, errorTaskIds } = this.state

    if (firmId == null) {
      return null
    }

    if (data == null || data.firm == null || data.firm.tasks == null) {
      return null
    }

    return (
      <>
        <H5>Which marketing source would you like to add?</H5>
        <Spacer height={16} />
        <TypeaheadBox<LeasingAttributionSource>
          loadFunc={this.marketingSourceLoad}
          rowFunc={this.marketingSourceRow}
          onChoose={this.onChooseMarketingSource}
          placeholder="Choose endpoint..."
          showResultsWhenEmpty
          clearOnBlur={false}
          hasError={errorSource !== ''}
        />
        {errorSource && renderFieldError(errorSource)}
        <Spacer height={64} />
        <H3>For which properties?</H3>
        <Spacer height={16} />
        <TasksTableMultiselect tasks={data.firm.tasks} onChange={this.onSelectedTaskIdsChange} />
        {errorTaskIds && renderFieldError(errorTaskIds)}
        <Spacer height={32} />
        <AlignRight>
          <Button disabled={submitting} onClick={this.onSaveUpdate}>
            Save Update
          </Button>
        </AlignRight>
      </>
    )
  }

  private marketingSourcesSuccess = () => {
    const { data } = this.props
    const { selectedSource, selectedTaskIds, updatedTasks } = this.state

    if (data == null) {
      return null
    }

    const tasksNotUpdated = getTasksNotUpdated(data.firm.tasks, selectedTaskIds, updatedTasks)

    return (
      <>
        <H1>Thank you!</H1>
        {updatedTasks.length > 0 && (
          <>
            <Spacer height={64} />
            <H3 color="silver">
              <BodyMonoTextSpan>{selectedSource}</BodyMonoTextSpan> source added to …
            </H3>
            <Spacer height={16} />
            {renderTasksList(updatedTasks)}
          </>
        )}
        {tasksNotUpdated.length > 0 && (
          <>
            <Spacer height={16} />
            <H3 color="silver">{selectedSource} source already exists on …</H3>
            <Spacer height={16} />
            {renderTasksList(tasksNotUpdated)}
          </>
        )}
      </>
    )
  }

  render() {
    const { saveSuccess } = this.state

    return saveSuccess ? this.marketingSourcesSuccess() : this.marketingSourcesForm()
  }
}

export default function MarketingSourcesSection(props: MarketingSourcesSectionProps) {
  const { firmId } = props

  if (firmId == null) {
    return null
  }

  const res = useQuery<FetchFirmTasksQuery, FetchFirmTasksQueryVariables>(fetchFirmTasksQuery, {
    variables: { firmId },
  })

  if (res.loading || res.error) {
    return null
  }

  return (
    <InlineErrorBoundary componentName="MarketingSourcesSection">
      <MarketingSourcesSectionImpl firmId={firmId} {...props} {...res} />
    </InlineErrorBoundary>
  )
}
